/**
 * Token-consumer view.
 *
 * On mount: read `?token=…` from the URL, POST to
 * `/internal/admin/auth/callback`, and on success refresh the auth
 * state (which sets `kind: authenticated` from the GET /me response)
 * + route to the accounts view. On failure surface "link invalid or
 * expired" and offer a way back to the sign-in form.
 *
 * The cookie is set server-side; the SPA stores nothing locally.
 *
 * Strips `?token=` from the URL on either branch so a refresh or a
 * back/forward navigation doesn't re-consume the (now-spent or
 * known-invalid) token.
 */

import { useEffect, useState } from 'react';

import { useAdminApi } from '../../api/api-context.js';
import { useAuth } from '../../app/auth-context.js';
import type { SetView } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface VerifyViewProps {
  setView: SetView;
}

export function VerifyView({ setView }: VerifyViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const [status, setStatus] = useState<'pending' | 'failed'>('pending');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');

    // Always strip the token from the URL — it's single-use.
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.toString());

    if (token === null || token.length === 0) {
      // No token in URL → render the failed branch and drop the
      // bootstrap-pending auth state so a "Back to sign in" click
      // doesn't land on the App-level "Loading admin…" splash
      // (`<App />` keeps the splash up while `state.kind === 'bootstrap'`,
      // and the verify view started under `skipAuthBootstrap=true`).
      auth.clear();
      setStatus('failed');
      return;
    }

    let cancelled = false;
    (async (): Promise<void> => {
      try {
        await api.consumeToken(token);
        if (cancelled) return;
        // Cookie is set; refresh the auth state from /me so
        // AuthProvider hydrates `staff` (kind=authenticated) and the
        // AccountsListView passes the App-level shell guard.
        //
        // CRITICAL: this `await` is what makes the post-callback flow
        // race-free. `setView('accounts')` MUST NOT fire until the
        // auth state has transitioned to `authenticated`, otherwise
        // the next render shows the AccountsListView under a
        // `bootstrap` auth state and the user sees the "Loading admin…"
        // splash → looks like the verify dropped them on the login.
        await auth.refresh();
        if (cancelled) return;
        setView('accounts');
      } catch {
        if (cancelled) return;
        // Consume failed (server 400 invalid_or_expired, network, etc).
        // Same reasoning as the no-token branch: drop the pending
        // auth state so the failed panel + Back-to-sign-in click
        // route the user cleanly to the login form.
        auth.clear();
        setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
    // Run once on mount — token, refresh, setView are all stable for
    // this lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-20">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        Verifying sign-in
      </div>
      {status === 'pending' ? (
        <p style={{ fontFamily: sansFont, fontSize: 15, color: C.text, lineHeight: 1.6 }}>
          Consuming your staff sign-in token…
        </p>
      ) : (
        <div
          className="p-6"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            Link invalid or expired
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              lineHeight: 1.6,
              marginBottom: 16,
            }}
          >
            That sign-in link is no longer valid. Staff sign-in links are single-use
            and expire after a short window — request a new one.
          </p>
          <button
            type="button"
            onClick={() => setView('login')}
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              padding: '8px 14px',
              borderRadius: 2,
              border: 0,
            }}
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}
